import { z } from 'zod'
import {
  createRequiredLatinCharacterSchema,
  createOptionalLatinCharacterSchema
} from '@/common'
import { GoodsFamily, ReportingPeriodBasis } from '@/common'

export const goodsFamilies: {
  value: GoodsFamily
  isReportingEnabled: boolean
}[] = [
  { value: GoodsFamily.ALUMINIUM, isReportingEnabled: true },
  { value: GoodsFamily.CEMENT, isReportingEnabled: false },
  { value: GoodsFamily.IRONANDSTEEL, isReportingEnabled: true },
  { value: GoodsFamily.ELECTRICITY, isReportingEnabled: false },
  { value: GoodsFamily.CHEMICALS, isReportingEnabled: false },
  { value: GoodsFamily.FERTILIZERS, isReportingEnabled: false }
]

export const installationGeneralInformationSchema = z.object({
  name: createRequiredLatinCharacterSchema('Installation name must use Latin characters', {
    min: 2
  }),
  localName: z.string().min(1),
  goodsFamily: z.enum([
    GoodsFamily.ALUMINIUM,
    GoodsFamily.CEMENT,
    GoodsFamily.IRONANDSTEEL,
    GoodsFamily.ELECTRICITY,
    GoodsFamily.CHEMICALS,
    GoodsFamily.FERTILIZERS
  ])
})

export const installationReportingPeriodBasisSchema = z.object({
  basisForReportingPeriod: z.enum([
    ReportingPeriodBasis.CALENDAR_YEAR,
    ReportingPeriodBasis.FISCAL_YEAR,
    ReportingPeriodBasis.DEFINED_BY_ELIGIBLE_MONITORING_AND_VERIFICATION_SCHEME
  ]),
  startMonth: z.number().min(0).max(11),
  monthInterval: z.number().min(3).max(12)
})

export const installationAddressSchema = z.object({
  street: createOptionalLatinCharacterSchema('Street address must use Latin characters', {
    max: 70
  }),
  street2: createOptionalLatinCharacterSchema('Street address must use Latin characters', {
    max: 70
  }),
  streetNumber: createOptionalLatinCharacterSchema('Street number must use Latin characters', {
    max: 35
  }),
  country: z.string().length(2),
  postCode: createOptionalLatinCharacterSchema('Postal code must use Latin characters', {
    max: 17
  }),
  poBox: createOptionalLatinCharacterSchema('P.O. Box must use Latin characters', { max: 70 }),
  city: createRequiredLatinCharacterSchema('City name must use Latin characters', {
    min: 2,
    max: 35
  }),
  unlocode: z.string().max(17).optional(),
  longitude: z.coerce.number().max(180).min(-180),
  latitude: z.coerce.number().max(90).min(-90)
})

export const installationLocalAddressSchema = z.object({
  localStreet: z.string().max(70).optional(),
  localStreetNumber: z.string().max(35).optional(),
  localPostCode: z.string().max(17).optional(),
  localCity: z.string().min(2).max(35),
  localPoBox: z.string().max(70).optional(),
  localStreet2: z.string().max(70).optional()
})

export type InstallationAddress = z.infer<typeof installationAddressSchema>
export type InstallationLocalAddress = z.infer<typeof installationLocalAddressSchema>

export const installationCreatePayloadSchema = z.object({
  ...installationGeneralInformationSchema.shape,
  ...installationLocalAddressSchema.shape,
  ...installationReportingPeriodBasisSchema.shape,
  ...installationAddressSchema.shape
})

export type InstallationCreatePayload = z.infer<typeof installationCreatePayloadSchema>

export interface Installation extends InstallationCreatePayload {
  id: string
  organizationId: string
  deletedAt: string | null
  createdAt: string
  updatedAt: string | null
}

export type InstallationWithResources = Installation & {
  reportingPeriods: {
    startDate: string
    endDate: string
  }[]
}
